<template>
  <!-- TODO
  - rename this to pasteMediaModal for v3
  - image paste when 24 hours active
  - enter on caption -->
  <b-modal
    id="image-paste-modal"
    ref="imagePasteModal"
    class="static-modal"
    title="Send media"
    ok-title="Send"
    hide-header-close
    static
    centered
    size="xl"
    @shown="onShow"
    @hidden="onHide"
    @ok="submitMedia"
    @cancel="cancelMedia"
    @hide="cancelMedia" >
    <div
      class="image-preview-wrapper" >
      <!-- Show name of the file in the right bottom corner only for image and video. -->
      <span
        v-if="selectedMediaFileId && (showImage || showVideo)"
        class="preview-file-name" >
        {{ processedMedia[selectedMediaFileId].name }}
      </span>
      <!-- Show image preview -->
      <div
        v-if="selectedMediaFileId && showImage"
        class="image-preview"
        :style="{backgroundImage: `url(${processedMedia[selectedMediaFileId].url})`}" />
      <!-- Show video preview -->
      <video
        v-else-if="selectedMediaFileId && showVideo"
        ref="videoSource"
        class="vue-lb-modal-video"
        controls
        autoplay
        loop
        :src="processedMedia[selectedMediaFileId].url"
        @click.stop >
        Your browser does not support the video tag.
      </video>
      <!-- Show audio preview -->
      <div
        v-if="selectedMediaFileId && showAudio"
        class="audio-preview flex column center-all" >
        <span class="title mb-3">{{ processedMedia[selectedMediaFileId].name }}</span>

        <SBAudioPlayer
          :type="processedMedia[selectedMediaFileId].mediaType"
          :name="processedMedia[selectedMediaFileId].name"
          :url="processedMedia[selectedMediaFileId].url" />
      </div>
      <!-- Show document preview -->
      <div
        v-if="selectedMediaFileId && ['file', 'pdf', 'excel', 'powerpoint', 'word', 'text'].includes(processedMedia[selectedMediaFileId].mediaType)"
        class="document-preview flex column center-all" >
        <SBIcon
          :icon="getMediaIcon(processedMedia[selectedMediaFileId].mediaType)"
          :size="40"
          :color="SBColors.greyBorder"
          @click="removeMedia(media.id)" />
        <span class="title mb-3">{{ processedMedia[selectedMediaFileId].name }}</span>
      </div>
    </div>
    <label
      :class="{ hide: disableCaption }"
      for="caption" >Caption</label>
    <MessageInput
      id="caption-input"
      ref="captionRef"
      name="caption"
      class="message-input"
      :class="{ hide: disableCaption }"
      :disabled="disableCaption"
      :focus-shadow="true"
      :hide-template-button="true"
      :hide-file-button="true"
      :hide-audio-button="true"
      :hide-send-button="true"
      :hide-quick-replies-button="hideQuickRepliesButton"
      :all-actions-to-right="true"
      :prevent-store-changes="!firstMediaSelected"
      :reply-to="messageToReply[activeConversation.id] ? messageToReply[activeConversation.id] : null"
      @input-change="updateMediaCaption" />
    <b-popover
      target="caption-input"
      triggers="hover"
      placement="auto"
      custom-class="big basic-popover"
      :disabled="!disableCaption">
      Caption is not supported for this file type
    </b-popover>
    <div
      v-if="!maxFiles || maxFiles > 1"
      class="thumb-scroll-wrapper flex flex-align-center">
      <ScrollArea
        v-dragscroll.x="true"
        class="horizontal thumb-scroll thin"
        marker-button-text="" >
        <div class="thumbs">
          <span
            v-for="media in processedMedia"
            :key="media.id"
            class="thumb"
            :style="{backgroundImage: `url(${media.thumbnail ? media.thumbnail : media.url})`}"
            @click.self="thumbSelected(media.id)">
            <span
              v-if="media.mediaType !== 'image'"
              class="thumb-overlay"
              :class="[media.mediaType]" />
            <SBIcon
              class="remove"
              :icon="SBIconList.CLOSE_THIN"
              :size="10"
              :color="SBColors.greyUltralight"
              :hover-size="20"
              :hover-icon-color="SBColors.linkHover"
              @click="removeMedia(media.id)" />
          </span>
        </div>
      </ScrollArea>
      <label
        v-if="!maxFilesReached"
        for="file">
        <span class="thumb-add no-remove-hover flex column center-all no-shrink">
          <SBIcon
            :icon="SBIconList.PLUS_THIN"
            :color="SBColors.bluePrimary"
            :size="18" />
        </span>
      </label>
    </div>
    <template #modal-footer="{ ok, cancel }">
      <transition name="fade">
        <div
          v-if="statsErrorMessage"
          class="greedy m-0">
          <span
            class="error-media" >{{ statsErrorMessage }}</span>
        </div>
      </transition>
      <!-- Emulate built in modal footer ok and cancel button actions -->
      <b-button
        variant="secondary"
        @click="cancel()">
        Cancel
      </b-button>
      <b-button
        variant="primary"
        @click="ok()">
        Send
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { getThumbnails } from 'video-metadata-thumbnails';
import { mapActions, mapGetters } from 'vuex';

import MessageInput from '@/components/business/chat/partials/MessageInput';
import ScrollArea from '@/components/common/ScrollArea';
import SBIcon from '@/components/common/Icon';
import SBIconList from '@/helpers/lists/sb-icon-list';
import SBColors from '@/helpers/lists/colors';
import { getFileExtension, getMediaType, getMediaTypeForLastMessage } from '@/helpers/mediaHelpers';
import SBAudioPlayer from '@/components/common/AudioPlayer';
import { whatsappFormatMessage } from '@/helpers/stringHelpers';
import ToasterMixin from '@/mixins/Toaster';

export default {
  name: 'PasteImageModal',
  components: {
    MessageInput,
    ScrollArea,
    SBAudioPlayer,
    SBIcon,
  },
  mixins: [
    ToasterMixin,
  ],
  props: {
    emitMode: {
      type: Boolean,
      default: false,
    },
    maxFiles: {
      type: Number,
      default: null,
    },
    maxFileErrorMessage: {
      type: String,
      required: false,
      default: null,
    },
    hideQuickRepliesButton: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // imageBlob: {},
      // videoBlob: {},
      disableCaption: false,
      showAudio: false,
      showImage: false,
      showVideo: false,
      visible: false,
      sending: false, // TODO will probably be removed
      caption: '',
      processedMedia: {},
      selectedMediaFileId: null,
      SBIconList,
      SBColors,
      statsErrorMessage: '',
      errorTimeout: null,
      errorMessageDuration: 5000,
      messageToReply: {},
    };
  },
  computed: {
    ...mapGetters({
      activeConversation: 'chat/activeConversation',
      me: 'organisation/me',
      inputMessageText: 'chat/inputMessageText',
    }),
    firstMediaSelected() {
      const mediaKeys = Object.keys(this.processedMedia);
      return (mediaKeys.length && mediaKeys[0] === this.selectedMediaFileId);
    },
    maxFilesReached() {
      const maxFilesAllowed = Number.parseInt(this.maxFiles, 10);
      return maxFilesAllowed && maxFilesAllowed > 0 && Object.keys(this.processedMedia).length >= maxFilesAllowed;
    },
  },
  watch: {
    '$store.state.chat.activeConversation'() {
      // Cancel media modal if conversation is changed while media modal is opened for previous conversation.
      if(this.visible) {
        this.cancelMedia();
        this.hide();
      }
    },
    selectedMediaFileId() {
      if (this.$refs.videoSource && this.selectedMediaFileId) {
        this.$refs.videoSource.src = this.processedMedia[this.selectedMediaFileId].url;
      }
    },
  },
  mounted() {
    this.$eventBus.$on('mediaDrop', this.mediaDropHandler);
    this.$eventBus.$on('dropStats', this.dropStats);
    this.$eventBus.$on('start-reply', this.setReplyMessage);
    this.$eventBus.$on('cancel-reply-to', () => { this.messageToReply[this.activeConversation.id] = null; });
    this.$eventBus.$on('addoutboundmessage', () => { this.messageToReply[this.activeConversation.id] = null; });
  },
  beforeDestroy() {
    this.$eventBus.$off('mediaDrop', this.mediaDropHandler);
    this.$eventBus.$off('dropStats', this.dropStats);
  },
  methods: {
    ...mapActions({
      sendMedia: 'chat/sendMediaV2',
      addMessageToFastLoad: 'chat/addMessageToFastLoad',
      clearInputMessage: 'chat/clearInputMessage',
    }),
    setReplyMessage(message) {
      this.messageToReply[this.activeConversation.id] = message;
      this.messageToReply = { ...this.messageToReply };
    },
    getMediaIcon(mediaType) {
      let icon = null;
      switch (mediaType) {
        case 'pdf':
          icon = this.SBIconList.PDF;
          break;
        case 'excel':
          icon = this.SBIconList.XLS;
          break;
        case 'powerpoint':
          icon = this.SBIconList.PPT;
          break;
        default:
          icon = this.SBIconList.DOC;
          break;
      }
      return icon;
    },
    /** Clears media data when modal is canceled. */
    cancelMedia() {
      this.selectedMediaFileId = null;
      this.processedMedia = {};
      this.statsErrorMessage = '';
    },
    dropStats(stats) {
      // If media modal is not opened and all the dropped files are unsupported, show only pill style error above the message input
      if (
        (stats.unsupportedMedia.length > 0) &&
        (stats.supportedMedia.length === 0) &&
        !this.visible
      ) {
        this.$eventBus.$emit('pillmessage', `${stats.unsupportedMedia.length} file${stats.unsupportedMedia.length > 1 ? 's' : ''} not added. The file format is not supported.`);
      } else if (stats.unsupportedMedia.length > 0) {
        // Media modal is open or it will be opened on the next cycle due to some of the files being supported
        this.statsErrorMessage = `${stats.unsupportedMedia.length} file${stats.unsupportedMedia.length > 1 ? 's' : ''} not added. The file format is not supported.`;
        clearTimeout(this.errorTimeout);
        this.errorTimeout = setTimeout(() => {
          this.statsErrorMessage = '';
        }, this.errorMessageDuration);
      }
    },
    /**
     * Handler for media drop event. Accepts single file or array of files.
     *
     * @param droppedMedia Media file(s) dropped on the messages section.
     */
    mediaDropHandler(droppedMedia) {
      if (Array.isArray(droppedMedia)) {
        droppedMedia.forEach((media) => {
          this.processMedia(media);
        });
      } else {
        this.processMedia(droppedMedia);
      }

      this.show('test');
    },
    /**
     * Processes media file setting it url, thumbnail for video, caption, type etc.
     *
     * @param media Media to process.
     */
    processMedia(media) {
      if (this.maxFilesReached) {
        if (this.maxFileErrorMessage) this.showToaster(this.maxFileErrorMessage, null, null, 'warning');
        return;
      }
      let newMedia = {};
      const URLObj = window.URL || window.webkitURL;
      const mediaType = media.file.type ? getMediaType(media.file.type) : getMediaType(getFileExtension(media.name));

      if (mediaType === 'video') {
        this.parseVideoThumbnails(media.id, media.file);
      }

      newMedia = {
        id: media.id,
        name: media.name,
        url: URLObj.createObjectURL(media.file),
        thumbnail: null,
        caption: '',
        mediaType,
        file: media.file,
      };

      this.processedMedia[media.id] = newMedia;
      this.processedMedia = { ...this.processedMedia };

      const mediaKeys = Object.keys(this.processedMedia);
      if (mediaKeys.length === 1 && this.inputMessageText) {
        this.processedMedia[mediaKeys[0]].caption = this.inputMessageText.toString();
      } else if (media.caption) {
        this.processedMedia[mediaKeys[0]].caption = media.caption;
      }
    },
    async parseVideoThumbnails(mediaId, videoBlob) {
      const thumbnails = await getThumbnails(videoBlob, {
        quality: 0.6,
        start: 0,
        end: 0,
        scale: 0.5,
      });
      const URLObj = window.URL || window.webkitURL;
      this.processedMedia[mediaId].thumbnail = URLObj.createObjectURL(thumbnails[0].blob);
    },
    /** Triggered on modal show event. */
    onShow() {
      this.visible = true;
      this.$eventBus.$emit('media-modal-opened');
    },
    /** Triggered on modal hide event. */
    onHide() {
      this.visible = false;
      this.sending = false;
      this.$eventBus.$emit('media-modal-closed');
    },
    /**
     * Removes the media from sending queue.
     *
     * @param mediaId Id of the media to be removed.
     */
    removeMedia(mediaId) {
      let currentlySelectedMediaIndex = Object.keys(this.processedMedia).findIndex(key => key === this.selectedMediaFileId);
      const mediaBeingDeletedIndex = Object.keys(this.processedMedia).findIndex(key => key === mediaId);
      // If not cleared before media is removed from processed data and that is the media we delete then there is a error in the console complaining that the media is undefined in the processed data.
      this.selectedMediaFileId = null;
      delete this.processedMedia[mediaId];
      this.processedMedia = { ...this.processedMedia };
      const mediaFilesKeys = Object.keys(this.processedMedia);
      if (mediaFilesKeys.length) {
        if (
          (currentlySelectedMediaIndex > mediaBeingDeletedIndex) || // When deleted media is on the left of the selected media, decrease the selected index so the currently selected remains selected.
          (mediaFilesKeys.length === currentlySelectedMediaIndex) // When the last media is selected and also deleted, change preview selection to the thumbnail before the deleted one.
        ) {
          currentlySelectedMediaIndex--;
        }
        this.thumbSelected(mediaFilesKeys[currentlySelectedMediaIndex]);
      } else {
        // When there is no media in the modal any more, run cancel on modal and hide it.
        this.cancelMedia();
        this.hide();
      }
    },
    submitMedia() {
      this.clearInputMessage();
      Object.keys(this.processedMedia).forEach((mediaKey) => {
        const formData = new FormData();

        const mediaCaption = this.processedMedia[mediaKey].caption ? whatsappFormatMessage(this.processedMedia[mediaKey].caption) : null;

        formData.append('file', this.processedMedia[mediaKey].file);
        formData.append('temp_sid', mediaKey);
        if (mediaCaption) {
          formData.append('caption', mediaCaption);
        }

        const mediaType = getMediaTypeForLastMessage(this.processedMedia[mediaKey].mediaType);

        const outboundMessageData = {
          sid: mediaKey,
          body: mediaCaption || (!['image', 'audio', 'video'].includes(this.processedMedia[mediaKey].mediaType) ? this.processedMedia[mediaKey].name : null),
          media_type: this.processedMedia[mediaKey].mediaType,
          media_url: this.processedMedia[mediaKey].url,
          loaded: false,
          direction: 'outbound',
          status: 'sending',
          timestamp: this.$moment().unix(),
          agent: true,
          sender: this.me.name,
          sender_icon: this.me.platform_id,
        };
        if (!mediaCaption && !['image', 'audio', 'video'].includes(mediaType)) {
          outboundMessageData.title = this.processedMedia[mediaKey].name;
        }

        if (this.messageToReply[this.activeConversation.id]) {
          outboundMessageData.context = this.messageToReply[this.activeConversation.id];
          formData.append('context_id', this.messageToReply[this.activeConversation.id].id);
        }

        // In emit mode no need to send the media to the server, just emit the media data to the parent component.
        if (this.emitMode) {
          this.$emit('media', {
            data: formData,
            message: outboundMessageData,
          });
          return;
        }

        this.$eventBus.$emit('addoutboundmessage', outboundMessageData);
        this.addMessageToFastLoad({
          message: outboundMessageData,
          conversation_uid: this.activeConversation.id,
        });

        this.sendMedia({
          merchant_id: this.$route.params.businessId || this.activeConversation.business_id,
          conversation_id: this.activeConversation.id,
          data: formData,
        });
      });
    },
    show(caption) {
      if (this.visible) {
        return;
      }
      this.thumbSelected(Object.keys(this.processedMedia)[0]);
      // if (imageBlob.type.indexOf('video') === -1) {
      //   this.imageBlob = imageBlob;
      // } else {
      //   this.videoBlob = imageBlob;
      // }

      this.$refs.imagePasteModal.show();
      this.$nextTick(() => {
        // TODO SET TEXT
        // this.$refs.messageArea.setText(caption);
        this.caption = caption;
      });
    },
    hide() {
      this.$refs.imagePasteModal.hide();
    },
    // TODO can we remove this? There is a media key in processed media map.
    /** Sets the type of the media currently in preview. */
    setPreviewType() {
      this.showAudio = false;
      this.showImage = false;
      this.showVideo = false;
      const selectedMedia = this.processedMedia[this.selectedMediaFileId];
      switch (selectedMedia.mediaType) {
        case 'image':
          this.showImage = true;
          break;
        case 'video':
          this.showVideo = true;
          break;
        case 'audio':
          this.showAudio = true;
          break;
        default:
          break;
      }
    },
    /**
     * Triggered when thumb for media is selected.
     *
     * @param mediaId Id of the media to be selected and shown in the preview.
     */
    thumbSelected(mediaId) {
      this.selectedMediaFileId = mediaId;
      this.$refs.captionRef.setHtml(this.processedMedia[mediaId].caption);
      // Set disabled after the caption text is reset. Without this the placeholder is not set if previously selected media had a caption populated and newly selected media is of type that has caption disabled.
      setTimeout(() => {
        this.disableCaption = !this.selectedMediaFileId || !['image', 'video'].includes(this.processedMedia[this.selectedMediaFileId].mediaType);
      }, 0);
      this.setPreviewType();
    },
    /**
     * Edits the caption for the image.
     *
     * @param caption Caption text to be set on the currently selected media.
     */
    updateMediaCaption(caption) {
      if (this.selectedMediaFileId) {
        this.processedMedia[this.selectedMediaFileId].caption = caption;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@mixin thumb-style {
  display: inline-block;
  margin-right: 8px;
  width: 64px;
  height: 64px;
  background-color: $white;
  background-size: cover;
  background-position: center;
  border-radius: 3px;
  cursor: pointer;
  position: relative;

  .thumb-overlay {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: $blackPrimary;
    mask-size: 25px 25px !important;
    mask-repeat: no-repeat;
    mask-position: center;

    &.audio {
      mask-image: url(~@/assets/img/SB-Icons/headphones.svg);
    }

    &.video {
      background-image: url(~@/assets/img/video-overlay.svg);
      background-size: 22px;
      background-repeat: no-repeat;
      background-position: center;
      background-color: rgba(20,28,32,.3);
      opacity: 1;
    }

    &.file, &.word, &.text {
      mask-image: url(~@/assets/img/SB-Icons/doc.svg);
    }

    &.powerpoint {
      mask-image: url(~@/assets/img/SB-Icons/ppt.svg);
    }

    &.excel {
      mask-image: url(~@/assets/img/SB-Icons/xls.svg);
    }

    &.pdf {
      mask-image: url(~@/assets/img/SB-Icons/pdf.svg);
    }
  }

  .remove {
    position: absolute;
    display: none;
    right: 0;
    z-index: 1;
  }

  &:hover {
    .remove {
      display: flex;
    }

    &:not(.no-remove-hover)::after {
      content: "";
      position: absolute;
      background: linear-gradient(200deg, rgba(20,28,32,0.75) 0%, rgba(255,255,255,0) 42%);
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      z-index: 0;
    }
  }
}

.fade-enter-active, .fade-leave-active {
  transition: opacity .25s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}

.image-preview-wrapper {
  display: flex;
  height: 377px;
  padding: 16px;
  border-radius: 5px;
  align-items: center;
  justify-content: center;
  background-color: $blackPrimary;
  position: relative;
  overflow: hidden;

  .preview-file-name {
    position: absolute;
    display: none;
    font-size: 10px;
    bottom: 0px;
    right: 0px;
    border-top-left-radius: 3px;
    color: $greyBorder;
    padding: 10px;
    background: $blackPrimary;
  }

  &:hover .preview-file-name {
    display: block;
  }

  .image-preview {
    width: 100%;
    height: 100%;
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
  }

  video {
    max-width: 100%;
  }

  .audio-preview, .document-preview {
    color: $greyBorder;
    .title {
      cursor: default;
      color: $greyBorder;
      text-align: center;
      font-size: 14px;
      word-break: break-all;
    }
  }

  .document-preview {
    .sb-icon {
      margin-bottom: 11px;
    }
  }
}

label {
  font-size: 14px !important;
  font-weight: 500 !important;
  line-height: 1.2 !important;
  color: $blackPrimary !important;
  text-transform: unset !important;
  margin: 16px 0 6px;
}

canvas {
  max-width: 100%;
  max-height: 100%;
}

.message-input {}

.thumb-scroll-wrapper {
  margin: 20px -20px -19px;
  padding: 8px 20px;
  background-color: $greyUltralight;
  box-shadow: 0px -1px $greyBorderModal;

  label[for="file"] {
    margin: 0 !important;

    .thumb-add {
      @include thumb-style;
      margin-right: 0;
      margin-left: 6px;
      border: dashed 1px #e1e5e6;

      span {
        font-size: 9px;
        font-weight: 500;
        line-height: 1.22;
        color: #2680eb;
        margin-top: 4px;
      }
    }
  }

  .thumb-scroll {
    // width: calc(100% + 40px);
    height: 80px;

    .thumbs {
      width: max-content;
      // min-width: calc(100% + 40px);
      height: 96px;
      // margin: 0 -20px -19px;
      padding: 8px 0;

      .thumb {
        @include thumb-style;
        border: solid 1px #e1e5e6;

        &:last-child {
          margin-right: 0;
        }
      }
    }
  }
}

.scroll-area.thin::-webkit-scrollbar {
  width: 4px;
  height: 4px;
}

// background-color: #f8fafb;
::v-deep .modal-body {
  background-color: $white !important;
}

.modal-footer {
  .error-media {
    position: relative;
    display: inline-flex;
    align-items: center;
    padding: 7px 10px;
    font-size: 12px;
    font-weight: 500;
    cursor: default;
    z-index: 50;
    border-radius: 50px;
    background: $redUltraLight;
    color: $redPrimary;
  }
}
</style>
